import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  DollarSign,
  Clock,
  Trash2,
  Loader2,
  ExternalLink,
  Save,
  User as UserIcon,
  Wallet,
  Plus,
  Ban,
} from "lucide-react";
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameMonth,
  subMonths,
  addMonths,
  parse,
} from "date-fns";
import ManualDepositPopover from "@/components/admin/ManualDepositPopover";
import AddonRequestDialog from "@/components/AddonRequestDialog";
import AddonRequestsList from "@/components/admin/AddonRequestsList";
import { Sparkles } from "lucide-react";
import { isTestAccount } from "@/lib/testAccounts";

interface Booking {
  id: string;
  service_name: string;
  service_category?: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  price: number;
  status: string;
  user_id: string;
  notes?: string | null;
  payment_status?: string;
  payment_type?: string | null;
  deposit_paid_amount?: number;
  checked_in_at?: string | null;
}

interface BlockedDate {
  id: string;
  blocked_date: string;
  all_day: boolean;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
}

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM",
];

const formatBlockTime = (t: string | null) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const BookingCalendar = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Auto-jump to next month when books are open (today >= auto-block unlock day).
  // Runs once on mount; admins can still navigate freely afterwards.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("site_settings")
          .select("key,value")
          .in("key", ["auto_block_enabled", "auto_block_unlock_day"]);
        const map = new Map((data ?? []).map((r: any) => [r.key, r.value]));
        const enabled = (map.get("auto_block_enabled") ?? "true") !== "false";
        const unlockDay = Math.max(
          1,
          Math.min(28, parseInt(map.get("auto_block_unlock_day") ?? "22", 10) || 22),
        );
        if (!enabled || cancelled) return;
        const today = new Date();
        if (today.getDate() >= unlockDay) {
          setCurrentDate(addMonths(new Date(today.getFullYear(), today.getMonth(), 1), 1));
        }
      } catch {
        // no-op
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [pendingAddons, setPendingAddons] = useState<Record<string, number>>({});
  const [confirmedAddons, setConfirmedAddons] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Appointment detail / edit dialog state
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [addonBooking, setAddonBooking] = useState<Booking | null>(null);
  const [editDate, setEditDate] = useState<Date | undefined>();
  const [editTime, setEditTime] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState<string>("confirmed");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Day detail dialog
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [blockBusyId, setBlockBusyId] = useState<string | null>(null);
  const [addingBlock, setAddingBlock] = useState(false);
  const [newBlockAllDay, setNewBlockAllDay] = useState(true);
  const [newBlockStart, setNewBlockStart] = useState("");
  const [newBlockEnd, setNewBlockEnd] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");

  const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const nextWeekStart = addWeeks(currentWeekStart, 1);
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const currentWeekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const nextWeekDays = Array.from({ length: 7 }, (_, i) => addDays(nextWeekStart, i));

  // Month grid
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = (getDay(monthStart) + 6) % 7; // Monday = 0
  const paddedMonthDays: (Date | null)[] = [
    ...Array.from({ length: startPadding }, () => null),
    ...monthDays,
  ];

  const fetchAll = async () => {
    setLoading(true);
    const start = format(monthStart, "yyyy-MM-dd");
    const nextWeekEnd = format(addDays(nextWeekStart, 6), "yyyy-MM-dd");
    const end = format(monthEnd, "yyyy-MM-dd");
    const fetchEnd = nextWeekEnd > end ? nextWeekEnd : end;

    const { data } = await supabase
      .from("bookings")
      .select("*").is("deleted_at", null)
      .gte("booking_date", start)
      .lte("booking_date", fetchEnd)
      .order("booking_time", { ascending: true });

    const bookingData = (data || []).filter((b: any) => !isTestAccount(b.user_id));
    setBookings(bookingData);

    const { data: blockedData } = await supabase
      .from("blocked_dates")
      .select("*")
      .gte("blocked_date", start)
      .lte("blocked_date", fetchEnd);
    setBlockedDates(blockedData || []);

    const userIds = [...new Set(bookingData.map((b) => b.user_id))];
    if (userIds.length > 0) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);

      const map: Record<string, string> = {};
      (profileData || []).forEach((p) => {
        map[p.user_id] = p.full_name || "Unknown";
      });
      setProfiles(map);
    }

    // Pending & confirmed add-ons across the visible bookings — used to
    // badge calendar cells. Confirmed add-ons also pulse so the admin
    // immediately sees that the booking's duration includes extras.
    const bookingIds = bookingData.map((b) => b.id);
    if (bookingIds.length > 0) {
      const { data: addonRows } = await supabase
        .from("booking_addons")
        .select("booking_id, status")
        .in("status", ["pending", "confirmed"])
        .in("booking_id", bookingIds);
      const pmap: Record<string, number> = {};
      const cmap: Record<string, number> = {};
      (addonRows || []).forEach((r: any) => {
        if (r.status === "pending") pmap[r.booking_id] = (pmap[r.booking_id] || 0) + 1;
        else if (r.status === "confirmed") cmap[r.booking_id] = (cmap[r.booking_id] || 0) + 1;
      });
      setPendingAddons(pmap);
      setConfirmedAddons(cmap);
    } else {
      setPendingAddons({});
      setConfirmedAddons({});
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const getBookingsForDay = (date: Date) =>
    bookings.filter((b) => isSameDay(new Date(b.booking_date + "T00:00:00"), date));

  const getBlocksForDay = (date: Date) =>
    blockedDates.filter((b) => isSameDay(new Date(b.blocked_date + "T00:00:00"), date));

  const getBookingsInRange = (days: Date[]) =>
    bookings.filter((b) =>
      days.some((d) => isSameDay(new Date(b.booking_date + "T00:00:00"), d))
    );

  const confirmedOnly = (list: Booking[]) => list.filter((b) => b.status === "confirmed");

  const revenue = (list: Booking[]) =>
    confirmedOnly(list).reduce((sum, b) => sum + (b.price || 0), 0);

  const currentWeekBookings = useMemo(() => getBookingsInRange(currentWeekDays), [bookings]);
  const nextWeekBookings = useMemo(() => getBookingsInRange(nextWeekDays), [bookings]);
  const monthBookings = useMemo(() => getBookingsInRange(monthDays), [bookings]);

  const isToday = (date: Date) => isSameDay(date, new Date());

  // Open appointment detail dialog and seed edit fields
  const openBookingDetail = (booking: Booking) => {
    setSelectedBooking(booking);
    setEditDate(new Date(booking.booking_date + "T00:00:00"));
    setEditTime(booking.booking_time);
    setEditNotes(booking.notes || "");
    setEditStatus(booking.status || "confirmed");
  };

  const closeDialog = () => {
    setSelectedBooking(null);
    setSaving(false);
    setDeleting(false);
  };

  const handleSave = async () => {
    if (!selectedBooking || !editDate || !editTime) return;
    setSaving(true);
    const newDate = format(editDate, "yyyy-MM-dd");
    const saveBooking = (override: boolean) =>
      supabase
        .from("bookings")
        .update({
          booking_date: newDate,
          booking_time: editTime,
          notes: editNotes,
          status: editStatus,
          blocked_day_override: override,
        })
        .eq("id", selectedBooking.id);

    let { error } = await saveBooking(false);

    if (error && /too close to another appointment|unavailable for booking/i.test(error.message)) {
      const confirmed = window.confirm(`${error.message}\n\nMove it here anyway?`);
      if (!confirmed) {
        setSaving(false);
        return;
      }
      ({ error } = await saveBooking(true));
    }

    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Appointment updated" });
    closeDialog();
    fetchAll();
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;
    setDeleting(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("bookings")
      .update({ deleted_at: new Date().toISOString(), deleted_by: auth?.user?.id ?? null })
      .eq("id", selectedBooking.id);
    setDeleting(false);
    setConfirmDelete(false);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: "Appointment deleted",
      description: "It stays on record in Bookings and can be undone there.",
    });
    closeDialog();
    fetchAll();
  };


  const openDayDetail = (date: Date) => {
    setSelectedDay(date);
    setAddingBlock(false);
    setNewBlockAllDay(true);
    setNewBlockStart("");
    setNewBlockEnd("");
    setNewBlockReason("");
  };

  const removeBlock = async (id: string) => {
    setBlockBusyId(id);
    const { error } = await supabase.from("blocked_dates").delete().eq("id", id);
    setBlockBusyId(null);
    if (error) {
      toast({ title: "Failed to remove block", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Block removed" });
    fetchAll();
  };

  const addBlock = async () => {
    if (!selectedDay) return;
    // Partial blocks must have explicit times — the standard block is all-day.
    if (!newBlockAllDay && (!newBlockStart || !newBlockEnd || newBlockEnd <= newBlockStart)) {
      toast({
        title: "Set a time range",
        description: "Choose a start and end time, or switch to blocking the entire day.",
        variant: "destructive",
      });
      return;
    }
    setBlockBusyId("__new__");
    const payload: any = {
      blocked_date: format(selectedDay, "yyyy-MM-dd"),
      all_day: newBlockAllDay,
      reason: newBlockReason || null,
    };
    if (!newBlockAllDay) {
      payload.start_time = newBlockStart;
      payload.end_time = newBlockEnd;
    }
    const { error } = await supabase.from("blocked_dates").insert(payload);
    setBlockBusyId(null);
    if (error) {
      toast({ title: "Failed to add block", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Block added" });
    setAddingBlock(false);
    setNewBlockReason("");
    fetchAll();
  };

  // One-click all-day block from the day dialog
  const quickBlockDay = async () => {
    if (!selectedDay) return;
    setBlockBusyId("__quick__");
    const { error } = await supabase.from("blocked_dates").insert({
      blocked_date: format(selectedDay, "yyyy-MM-dd"),
      all_day: true,
      reason: null,
    });
    setBlockBusyId(null);
    if (error) {
      toast({ title: "Failed to block day", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Day blocked" });
    fetchAll();
  };

  const WeekRow = ({ days, label }: { days: Date[]; label: string }) => (
    <div className="space-y-3">
      <h2 className="font-serif text-lg font-semibold italic">{label}</h2>
      <p className="text-xs text-muted-foreground">
        {format(days[0], "MMM d")} — {format(days[6], "MMM d, yyyy")}
      </p>
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const dayBookings = getBookingsForDay(day);
          const dayBlocks = getBlocksForDay(day);
          const today = isToday(day);
          const isEmpty = dayBookings.length === 0 && dayBlocks.length === 0;
          return (
            <Card
              key={day.toISOString()}
              onClick={() => openDayDetail(day)}
              className={`bg-card border-border/50 min-h-[160px] cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all ${today ? "ring-1 ring-primary" : ""}`}
              title="View day details"
            >
              <div
                className={`w-full px-2 py-1.5 border-b border-border/30 text-center ${today ? "bg-primary/10" : ""}`}
              >
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground">{format(day, "EEE")}</p>
                <p className={`text-lg font-bold ${today ? "text-primary" : ""}`}>{format(day, "d")}</p>
              </div>
              <CardContent className="p-1.5 space-y-1">
                {isEmpty ? (
                  <p className="w-full text-[10px] text-muted-foreground/50 text-center pt-4">
                    No bookings
                  </p>
                ) : (
                  <>
                    {dayBlocks.map((blk) => (
                      <div
                        key={blk.id}
                        className="p-1.5 text-[10px] rounded-sm border bg-destructive/15 border-destructive/40 text-destructive"
                        title={blk.reason || "Blocked"}
                      >
                        <p className="font-bold uppercase tracking-wider text-[9px]">
                          🚫 Blocked
                        </p>
                        <p className="truncate">
                          {blk.all_day
                            ? "All day"
                            : `${formatBlockTime(blk.start_time)} – ${formatBlockTime(blk.end_time)}`}
                        </p>
                        {blk.reason && (
                          <p className="truncate opacity-80">{blk.reason}</p>
                        )}
                      </div>
                    ))}
                    {dayBookings.map((b) => {
                      const isConfirmed = b.status === "confirmed";
                      const addonCount = pendingAddons[b.id] || 0;
                      const confirmedAddonCount = confirmedAddons[b.id] || 0;
                      return (
                        <div
                          key={b.id}
                          className={`relative p-1.5 text-[10px] rounded-sm border cursor-pointer hover:ring-1 hover:ring-primary/50 transition-all ${
                            isConfirmed
                              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                              : "bg-destructive/10 border-destructive/30 line-through opacity-60"
                          } ${confirmedAddonCount > 0 ? "ring-2 ring-amber-400/70 animate-pulse" : ""}`}
                          onClick={(e) => { e.stopPropagation(); openBookingDetail(b); }}
                        >
                          {addonCount > 0 && (
                            <span
                              title={`${addonCount} pending add-on request${addonCount > 1 ? "s" : ""}`}
                              className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-primary text-primary-foreground rounded-full shadow-sm"
                            >
                              +{addonCount}
                            </span>
                          )}
                          {confirmedAddonCount > 0 && (
                            <span
                              title={`Includes ${confirmedAddonCount} confirmed add-on${confirmedAddonCount > 1 ? "s" : ""} — duration already adjusted`}
                              className="absolute -top-1 -left-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-amber-500 text-white rounded-full shadow-sm"
                            >
                              ✨
                            </span>
                          )}
                          <p className="font-medium truncate flex items-center gap-0.5">
                            {b.booking_time}
                            {b.checked_in_at && (
                              <span className="font-bold">✓</span>
                            )}
                          </p>
                          {isConfirmed && (
                            <p className="text-[9px] uppercase tracking-wider opacity-80">Scheduled</p>
                          )}
                          <p className="truncate text-muted-foreground">{b.service_name}</p>
                          <p className="truncate text-muted-foreground">{profiles[b.user_id] || "—"}</p>
                        </div>
                      );
                    })}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">{format(currentDate, "MMMM yyyy")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="text-xs tracking-widest uppercase gap-1.5"
            onClick={() => navigate("/book")}
          >
            <Plus size={14} />
            New booking
          </Button>
          <Button variant="outline" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
            <ChevronLeft size={16} />
          </Button>
          <Button variant="outline" size="sm" className="text-xs tracking-widest uppercase" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "This Week", bookings: currentWeekBookings, dates: `${format(currentWeekDays[0], "MMM d")} – ${format(currentWeekDays[6], "MMM d")}` },
              { label: "Next Week", bookings: nextWeekBookings, dates: `${format(nextWeekDays[0], "MMM d")} – ${format(nextWeekDays[6], "MMM d")}` },
              { label: format(currentDate, "MMMM"), bookings: monthBookings, dates: format(currentDate, "MMMM yyyy") },
            ].map((s) => (
              <Card key={s.label} className="bg-card border-border/50">
                <CardContent className="p-5">
                  <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground/70 mb-3">{s.dates}</p>
                  <div className="flex items-end justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-primary" />
                      <span className="text-2xl font-bold">{confirmedOnly(s.bookings).length}</span>
                      <span className="text-xs text-muted-foreground">bookings</span>
                    </div>
                    <div className="flex items-center gap-1 text-primary">
                      <DollarSign size={14} />
                      <span className="text-xl font-bold">{revenue(s.bookings).toFixed(0)}</span>
                    </div>
                  </div>
                  {s.bookings.filter((b) => b.status !== "confirmed").length > 0 && (
                    <p className="text-[10px] text-destructive mt-2">
                      {s.bookings.filter((b) => b.status !== "confirmed").length} cancelled
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Current Week */}
          <WeekRow days={currentWeekDays} label="This Week" />

          {/* Next Week */}
          <WeekRow days={nextWeekDays} label="Next Week" />

          {/* Full Month Grid */}
          <div className="space-y-3">
            <h2 className="font-serif text-lg font-semibold italic">{format(currentDate, "MMMM yyyy")}</h2>
            <div className="grid grid-cols-7 gap-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="text-center text-[10px] tracking-widest uppercase text-muted-foreground py-1">
                  {d}
                </div>
              ))}
              {paddedMonthDays.map((day, i) => {
                if (!day) return <div key={`pad-${i}`} className="min-h-[80px]" />;
                const dayBookings = getBookingsForDay(day);
                const dayBlocks = getBlocksForDay(day);
                const isAutoBlock = (b: BlockedDate) =>
                  (b.reason ?? "").startsWith("AUTO_BLOCK:");
                const hasAutoFullBlock = dayBlocks.some((b) => b.all_day && isAutoBlock(b));
                const hasFullBlock = dayBlocks.some((b) => b.all_day);
                const hasManualBlock = dayBlocks.some((b) => !isAutoBlock(b));
                const today = isToday(day);
                const dayRevenue = revenue(dayBookings);
                const dayPendingAddons = dayBookings.reduce(
                  (sum, b) => sum + (pendingAddons[b.id] || 0),
                  0
                );
                return (
                  <div
                    key={day.toISOString()}
                    onClick={(e) => {
                      // Only open day detail if click was on the cell background (not an inner booking)
                      if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.daycell === "1") {
                        openDayDetail(day);
                      }
                    }}
                    className={`relative min-h-[80px] border rounded-sm p-1 cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all ${
                      hasAutoFullBlock
                        ? "bg-destructive/10 border-destructive/40"
                        : hasManualBlock
                          ? "bg-blue-500/10 border-blue-500/40"
                          : today
                            ? "ring-1 ring-primary bg-primary/5 border-border/30"
                            : "bg-card/50 border-border/30"
                    }`}
                  >
                    {dayPendingAddons > 0 && (
                      <span
                        title={`${dayPendingAddons} pending add-on request${dayPendingAddons > 1 ? "s" : ""}`}
                        className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-primary text-primary-foreground rounded-full shadow-sm z-10"
                      >
                        +{dayPendingAddons}
                      </span>
                    )}
                    <p data-daycell="1" className={`text-xs font-medium mb-0.5 ${today ? "text-primary" : hasAutoFullBlock ? "text-destructive" : hasManualBlock ? "text-blue-600 dark:text-blue-400" : "text-foreground"}`}>
                      {format(day, "d")}
                    </p>
                    {dayBlocks.length > 0 && (
                      <p data-daycell="1" className={`text-[9px] font-bold uppercase tracking-wider ${hasAutoFullBlock ? "text-destructive" : "text-blue-600 dark:text-blue-400"}`}>
                        🚫 {hasFullBlock ? "Blocked" : `${dayBlocks.length} block`}
                      </p>
                    )}
                    {dayBookings.length > 0 && (
                      <div data-daycell="1" className="space-y-0.5">
                        <p data-daycell="1" className="text-[9px] text-emerald-600 dark:text-emerald-400 font-medium">
                          {confirmedOnly(dayBookings).length} appt{confirmedOnly(dayBookings).length !== 1 ? "s" : ""}
                        </p>
                        {dayRevenue > 0 && (
                          <p data-daycell="1" className="text-[9px] text-muted-foreground">${dayRevenue.toFixed(0)}</p>
                        )}
                        {dayBookings.slice(0, 2).map((b) => (
                          <div
                            key={b.id}
                            className={`text-[8px] truncate cursor-pointer transition-colors ${
                              b.status === "confirmed"
                                ? "text-emerald-600 dark:text-emerald-400 hover:opacity-80"
                                : "text-muted-foreground line-through hover:text-primary"
                            }`}
                            onClick={(e) => { e.stopPropagation(); openBookingDetail(b); }}
                          >
                            {b.booking_time} {profiles[b.user_id]?.split(" ")[0] || ""}
                          </div>
                        ))}
                        {dayBookings.length > 2 && (
                          <p data-daycell="1" className="text-[8px] text-muted-foreground">+{dayBookings.length - 2} more</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Appointment Detail / Edit Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="bg-card border-border/50 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <CalendarDays size={20} className="text-primary" />
              Appointment Details
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {selectedBooking && (
                <>
                  {selectedBooking.service_name} — {profiles[selectedBooking.user_id] || "Client"}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4 py-2">
              {/* Read-only summary */}
              <div className="border border-border/50 rounded-md p-3 space-y-1.5 text-xs bg-background/50">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium">{selectedBooking.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span>{selectedBooking.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price</span>
                  <span>${Number(selectedBooking.price).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment</span>
                  <span className="capitalize">
                    {selectedBooking.payment_status || "pending"}
                    {selectedBooking.payment_type ? ` · ${selectedBooking.payment_type}` : ""}
                  </span>
                </div>
                {Number(selectedBooking.deposit_paid_amount) > 0 ? (
                  <ManualDepositPopover
                    booking={selectedBooking}
                    onSaved={() => {
                      // Refresh calendar; the dialog stays open with the previous values until the user closes it.
                      fetchAll();
                    }}
                    align="end"
                    trigger={
                      <button
                        type="button"
                        className="w-full flex justify-between items-center text-emerald-600 dark:text-emerald-400 hover:opacity-80 transition-opacity"
                        title="Click to edit deposit"
                      >
                        <span>Deposit Paid</span>
                        <span className="underline-offset-2 hover:underline">
                          ${Number(selectedBooking.deposit_paid_amount).toFixed(2)}
                        </span>
                      </button>
                    }
                  />
                ) : (
                  <ManualDepositPopover
                    booking={selectedBooking}
                    onSaved={() => fetchAll()}
                    align="end"
                    trigger={
                      <button
                        type="button"
                        className="w-full flex justify-between items-center text-muted-foreground hover:text-primary transition-colors"
                        title="Click to add a deposit"
                      >
                        <span>Deposit Paid</span>
                        <span className="text-[11px] uppercase tracking-wider underline-offset-2 hover:underline">
                          + Add deposit
                        </span>
                      </button>
                    }
                  />
                )}
                {selectedBooking.checked_in_at && (
                  <div className="flex justify-between text-primary">
                    <span>Checked In</span>
                    <span>{format(new Date(selectedBooking.checked_in_at), "MMM d, h:mm a")}</span>
                  </div>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                  Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-sm font-normal bg-background border-border/50">
                      <CalendarDays size={14} className="mr-2" />
                      {editDate ? format(editDate, "EEEE, MMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card" align="start">
                    <Calendar
                      mode="single"
                      selected={editDate}
                      onSelect={setEditDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time */}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                  Time
                </label>
                <Select value={editTime} onValueChange={setEditTime}>
                  <SelectTrigger className="bg-background border-border/50 text-sm">
                    <Clock size={14} className="mr-2" />
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="bg-card max-h-72">
                    {/* Include the current time even if not in standard slots */}
                    {!TIME_SLOTS.includes(editTime) && editTime && (
                      <SelectItem value={editTime}>{editTime} (current)</SelectItem>
                    )}
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                  Status
                </label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="bg-background border-border/50 text-sm capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1 block">
                  Notes <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
                </label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes about this appointment…"
                  className="bg-background border-border/50 text-sm min-h-[70px]"
                />
              </div>

              {/* Quick links */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[11px] gap-1"
                  onClick={() => {
                    const id = selectedBooking.user_id;
                    closeDialog();
                    navigate(`/admin/clients/${id}`);
                  }}
                >
                  <UserIcon size={12} /> Client Profile
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[11px] gap-1"
                  onClick={() => {
                    closeDialog();
                    navigate(`/admin/bookings?focus=${selectedBooking.id}`);
                  }}
                >
                  <ExternalLink size={12} /> Open in Bookings
                </Button>
                <Button
                  size="sm"
                  className="text-[11px] gap-1"
                  onClick={() => {
                    closeDialog();
                    navigate(`/admin/bookings?focus=${selectedBooking.id}&pay=${selectedBooking.id}`);
                  }}
                >
                  <Wallet size={12} /> Collect Payment
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-[11px] gap-1"
                  onClick={() => setAddonBooking(selectedBooking)}
                >
                  <Sparkles size={12} /> Add-on
                </Button>
              </div>

              <AddonRequestsList bookingId={selectedBooking.id} onChange={fetchAll} />
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              disabled={saving || deleting}
              className="text-xs tracking-widest uppercase gap-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 size={14} /> Delete
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={closeDialog}
                disabled={saving || deleting}
                className="text-xs tracking-widest uppercase text-muted-foreground"
              >
                Close
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || deleting || !editDate || !editTime}
                className="text-xs tracking-widest uppercase gap-1"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-card border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif italic">Delete this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the booking. The client will not be notified automatically.
              {selectedBooking && (
                <span className="block mt-2 text-foreground">
                  {selectedBooking.service_name} · {format(new Date(selectedBooking.booking_date + "T00:00:00"), "MMM d, yyyy")} at {selectedBooking.booking_time}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddonRequestDialog
        booking={addonBooking}
        open={!!addonBooking}
        onOpenChange={(o) => !o && setAddonBooking(null)}
        asAdmin
        onSubmitted={fetchAll}
      />

      {/* Day Detail Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="bg-card border-border/50 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-bold italic flex items-center gap-2">
              <CalendarDays size={20} className="text-primary" />
              {selectedDay && format(selectedDay, "EEEE, MMMM d, yyyy")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {selectedDay && (() => {
                const blocks = getBlocksForDay(selectedDay);
                const bks = getBookingsForDay(selectedDay);
                const fullBlock = blocks.some((b) => b.all_day);
                if (fullBlock) return "Day fully blocked";
                if (blocks.length > 0) return `${blocks.length} time block${blocks.length > 1 ? "s" : ""} · ${bks.length} appointment${bks.length !== 1 ? "s" : ""}`;
                return `${bks.length} appointment${bks.length !== 1 ? "s" : ""}`;
              })()}
            </DialogDescription>
          </DialogHeader>

          {selectedDay && (
            <div className="space-y-4 py-2">
              {/* Blocks */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Blocks</p>
                  {!addingBlock && (
                    <div className="flex items-center gap-1">
                      {selectedDay && !getBlocksForDay(selectedDay).some((b) => b.all_day) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-[11px] gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={quickBlockDay}
                          disabled={blockBusyId === "__quick__"}
                          title="Block the entire day in one click"
                        >
                          {blockBusyId === "__quick__" ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Ban size={12} />
                          )}
                          Block this day
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setAddingBlock(true)}>
                        + Add block
                      </Button>
                    </div>
                  )}
                </div>
                {getBlocksForDay(selectedDay).length === 0 && !addingBlock && (
                  <p className="text-xs text-muted-foreground italic px-1">No blocks for this day</p>
                )}
                {getBlocksForDay(selectedDay).map((blk) => (
                  <div key={blk.id} className="flex items-center justify-between p-2 border border-destructive/30 bg-destructive/10 rounded-sm text-xs">
                    <div className="min-w-0">
                      <p className="font-bold text-destructive">
                        🚫 {blk.all_day ? "All day" : `${formatBlockTime(blk.start_time)} – ${formatBlockTime(blk.end_time)}`}
                      </p>
                      {blk.reason && <p className="text-muted-foreground truncate">{blk.reason}</p>}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-destructive"
                      disabled={blockBusyId === blk.id}
                      onClick={() => removeBlock(blk.id)}
                    >
                      {blockBusyId === blk.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </Button>
                  </div>
                ))}

                {addingBlock && (
                  <div className="space-y-2 p-3 border border-border/50 rounded-sm bg-background/50">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="all-day"
                        checked={newBlockAllDay}
                        onChange={(e) => setNewBlockAllDay(e.target.checked)}
                      />
                      <label htmlFor="all-day" className="text-xs">All day</label>
                    </div>
                    {!newBlockAllDay && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start</label>
                          <Input type="time" value={newBlockStart} onChange={(e) => setNewBlockStart(e.target.value)} className="h-8 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">End</label>
                          <Input type="time" value={newBlockEnd} onChange={(e) => setNewBlockEnd(e.target.value)} className="h-8 text-sm" />
                        </div>
                      </div>
                    )}
                    <Input
                      placeholder="Reason (optional)"
                      value={newBlockReason}
                      onChange={(e) => setNewBlockReason(e.target.value)}
                      className="h-8 text-sm"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setAddingBlock(false)} disabled={blockBusyId === "__new__"}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={addBlock} disabled={blockBusyId === "__new__"}>
                        {blockBusyId === "__new__" ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                        Block
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Appointments */}
              <div className="space-y-2">
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Appointments</p>
                {getBookingsForDay(selectedDay).length === 0 ? (
                  <p className="text-xs text-muted-foreground italic px-1">No appointments scheduled</p>
                ) : (
                  getBookingsForDay(selectedDay).map((b) => {
                    const isConfirmed = b.status === "confirmed";
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => {
                          setSelectedDay(null);
                          openBookingDetail(b);
                        }}
                        className={`w-full text-left p-2 border rounded-sm text-xs transition-colors ${
                          isConfirmed
                            ? "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20"
                            : "bg-muted/30 border-border/30 hover:bg-muted/50 line-through opacity-70"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{b.booking_time} · {b.service_name}</span>
                          <span className="text-muted-foreground">${Number(b.price).toFixed(0)}</span>
                        </div>
                        <p className="text-muted-foreground capitalize">
                          {profiles[b.user_id] || "—"} · {b.status}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            {selectedDay && (
              <Button
                size="sm"
                className="text-xs tracking-widest uppercase gap-1.5"
                onClick={() => {
                  const d = selectedDay;
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  setSelectedDay(null);
                  navigate(`/book?date=${iso}`);
                }}
              >
                <Plus size={14} />
                Book on this day
              </Button>
            )}
            <Button variant="ghost" onClick={() => setSelectedDay(null)} className="text-xs tracking-widest uppercase">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingCalendar;
